import React, { type FC, useMemo } from 'react'

import { Capability, createIsAuthenticatedWithCapabilitiesSelector, RuleEngineType, Translation } from '../../../../domain'

import { ToggleButton, ToggleButtonGroup } from '../../common'
import { useTranslator } from '../../Internationalization'
import { useAppState, useAppUsecase } from '../../State'
import { useSelectedType } from './Hooks'

const canReadResources = createIsAuthenticatedWithCapabilitiesSelector(Capability.RULE_ENGINE_RESOURCES)

export const TypeSelector: FC = () => {
    const filter = useAppUsecase('filterSandboxUsecase')
    const type = useSelectedType()
    const translator = useTranslator()
    const canLoadEndpoints = useAppState(canReadResources)

    const options = useMemo(
        () => [
            { label: translator.formatTranslation(Translation.JSON), value: RuleEngineType.JSON },
            ...(canLoadEndpoints ? [{ label: translator.formatTranslation(Translation.ENDPOINT, { count: 1 }), value: RuleEngineType.ENDPOINT }] : []),
            { label: translator.formatTranslation(Translation.MQTT_TOPIC), value: RuleEngineType.MQTT_TOPIC },
        ],
        [translator, canLoadEndpoints]
    )

    return (
        <ToggleButtonGroup
            data-testid="rule-engine-type"
            value={type}
            exclusive
            /**
             * When we click the same button again, MUI returns null (deselects it).
             * We only accept a valid type, ignoring null to keep a selection active.
             */
            onChange={(_, newType: RuleEngineType | null) => newType && filter.selectType(newType)}
        >
            {options.map((option) => (
                <ToggleButton key={option.value} value={option.value} aria-label={option.label} data-testid={`type-selector-${option.value}`}>
                    {option.label}
                </ToggleButton>
            ))}
        </ToggleButtonGroup>
    )
}
